import {Injectable, Injector} from '@angular/core';
import {NotificationBaseService} from './notification-base.service';
import {Observable, throwError, timer} from 'rxjs';
import {ListModel} from '@shared/models/list.model';
import {Utilities} from '@shared/utilities/utilities.model';
import {map} from 'rxjs/internal/operators';
import {UserV3Service} from '@shared/service/user-v-3.service';
import {switchMap, takeWhile} from 'rxjs/operators';
import {NotificationModel, NotificationType} from '../models/notification.model';
import {Response} from '@angular/http';

@Injectable({
	providedIn: 'root'
})
export class NotificationsService extends NotificationBaseService {

	protected BASE_TIMER: number = 300000;
	private unreadAlive: boolean;

	constructor(
		protected injector: Injector,
		private userV3Service: UserV3Service) {
		super(injector);
	}

	getUnreadList(types?: NotificationType[], filter?: any): Observable<NotificationModel[]> {
		let url: string = this.environment.NOTIFICATION + '/notifications/unread';
		if (!filter) {
			filter = {};
		}
		filter.types = types ? types : Object.keys(NotificationType).map(value => NotificationType[value]);
		url += Utilities.getQuery(filter);
		return <Observable<NotificationModel[]>>this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			map((response: any) => {
				if (!response.ok) {
					return throwError(response);
				}
				return response.json().items;
			}));
	}

	startUnreadNotificationPolling(filter?: any): Observable<NotificationModel[]> {
		this.unreadAlive = true;
		return timer(0, this.BASE_TIMER).pipe(
			takeWhile(() => this.unreadAlive),
			switchMap(() => this.getUnreadList(filter))
		);
	}

	stopUnreadNotificationPolling(): void {
		this.unreadAlive = false;
	}

	getList(types?: NotificationType[], filter?: any): Observable<GenericListModel> {
		let url: string = this.environment.NOTIFICATION + '/notifications/all';
		if (!filter) {
			filter = {};
		}
		filter.types = types ? types : Object.keys(NotificationType).map(value => NotificationType[value]);
		url += Utilities.getQuery(filter);
		return <Observable<GenericListModel>>this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			map((response: any) => {
				if (!response.ok) {
					return throwError(response);
				}
				return new GenericListModel(response.json());
			}));
	}

	getBreakingNewsList(filter?: any): Observable<GenericListModel> {
		let url: string = this.environment.NOTIFICATION + '/notifications/breaking_news';
		if (filter) {
			url += Utilities.getQuery(filter);
		}
		return <Observable<GenericListModel>>this.httpAuthService.get(url, this.environment.ROOT_REDIRECT).pipe(
			map((response: any) => {
				if (!response.ok) {
					return throwError(response);
				}
				return new GenericListModel(response.json());
			}));
	}

	create(notification: NotificationModel): Observable<any> {
		const url: string = this.environment.NOTIFICATION + '/notifications/breaking_news';
		const options = Utilities.setRequestOptionAUTH();
		const user = this.userV3Service.getCurrentUser().id;
		options.headers.append('nemo-user-id', user );
		return this.httpAuthService.post(url, notification, this.environment.ROOT_REDIRECT, options);
	}

	setRead(id_list: string[]): Observable<any> {
		const url: string = this.environment.NOTIFICATION + '/notifications/setread';
		return this.httpAuthService.post(url, {notification_ids: id_list}, this.environment.ROOT_REDIRECT);
	}

	update(notification: NotificationModel, id: string): Observable<any> {
		const url: string = this.environment.NOTIFICATION + '/notifications/breaking_news' + '/' + id;
		const options = Utilities.setRequestOptionAUTH();
		const user = this.userV3Service.getCurrentUser().id;
		options.headers.append('nemo-user-id', user );
		return this.httpAuthService.put(url, notification, this.environment.ROOT_REDIRECT, options);
	}

	getByUUID(uuid: string): Observable<any> {
		const url: string = this.environment.NOTIFICATION + '/notifications' + '/' + uuid;
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT);
	}

	delete(uuid: string): Observable<any> {
		const url: string = this.environment.NOTIFICATION + '/notifications/breaking_news' + '/' + uuid;
		return this.httpAuthService.delete(url, this.environment.ROOT_REDIRECT);
	}

	getNotificationStats(notification: NotificationModel): Observable<any> {
		const url: string = `${this.environment.NOTIFICATION}/notifications/${notification.type}/${notification.id}/stats`;
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT);
	}

	getBreakingNewsStats(): Observable<Response> {
		const url: string = `${this.environment.NOTIFICATION}/notifications/breaking_news/stats`;
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT);
	}

	blockNotify(id: string): Observable<any> {
		const url: string = this.environment.NOTIFICATION + '/notifications/breaking_news' + '/' + id + '/stop';
		return this.httpAuthService.put(url, {}, this.environment.ROOT_REDIRECT);
	}

	exportNotifyUserView(id: string): Observable<any> {
		const url: string = this.environment.NOTIFICATION + '/notifications/breaking_news' + '/' + id + '/export';
		return this.httpAuthService.get(url, this.environment.ROOT_REDIRECT);
	}
}

export class GenericListModel extends ListModel {
	constructor(model?: any) {
		super(model);
		this.items = [];
		if (!Utilities.isVoid(model) && !Utilities.isVoid(model.items)) {
			this.items = [...model.items];
		}
	}
}
